import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Body from '../Components/layout/Body';
import LearnMoreModal from '../Components/layout/LearnMoreModal';
import { SUBTABS, CHOOSE_STEPS } from './ConstPaths';
import TestDriveSetupSidebar from './TestDriveSetupSidebar';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const SANDBOX_HELP = `
  <div>
    <p><strong>Purpose:</strong> To make sure everything is set up properly to help enhance success during the Test-drive.</p>
    <p>A little preparation can go a long way here.  Focus on 3 main things:
    <ol>
      <li>Set up the access method, if it needs it.  Make all the necessary connections, try it out quickly to make sure it is working properly.</li>
      <li>Use the client's seating / positioning devices to get the best available stability and support in preparation for the Test-drive. Look for an active, functional, and comfortable posture.</li> 
      <li>Make sure the client understands the basics of using the access method.</li>
    </ol>
    </p>
    <p>
    If your access method doesn’t need any setup (like if you’re using a built-in trackpad with the default settings), you’re all set with the first goal.  
    Otherwise, set up the access method on the computer / device you’ll use for the Test-drive.  
    Try it out yourself to make sure it works. 
    If you’re running the Test-drive on this computer (the same device you’re using to run Access Navigator), use the Check Your Setup feature to make sure it’s working OK. 
    Or just try it quickly in any app that you want.  
    </p>
    <p><strong>Note:</strong> If you suspect the client is struggling due to instability or other seating issues, get some help to problem-solve this. Refer to your seating / positioning specialist, an ATP (assistive technology professional), or interdisciplinary team.</p> 
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  activeAccessMethodName: PropTypes.string,
  activeSetupSelectionMethod: PropTypes.string,
  activeSetupAccessories: PropTypes.arrayOf(PropTypes.string),
  activeSetupBodySite: PropTypes.arrayOf(PropTypes.string),
  activeSetupName: PropTypes.string,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  activeAccessMethodName: '',
  activeSetupSelectionMethod: '',
  activeSetupBodySite: [],
  activeSetupAccessories: [],
  activeSetupName: '',
};

//content sub-component with unique content for this page
function TestDriveSetupSandboxContent(props) {
  const activeAccessMethodName = props.activeAccessMethodName;
  const choose_path = SUBTABS.CHOOSE + "/" + CHOOSE_STEPS.ACCESS_METHOD;

  const ACCESS_TESTER_HTML = `
    <div style="padding: 20px 0;">
      <p><strong>Quick test for keypresses</strong></p>
      <label for="type-tester">Try typing here:</label>
      <input id="type-tester" type="text" style="width:100%;"></input>
    </div>
    <hr />
    <div style="padding: 20px 0;">
      <p><strong>Quick test for mouse movement and clicking</strong></p>
      <button id="mouse-tester" style="color:white; background-color: blue; padding: 20px; width:100%;" 
        onclick="
          const COLOR1 = 'blue';
          const COLOR2 = 'green';
          var nextColor;
          let currentColor = document.getElementById('mouse-tester').style.backgroundColor;
          (currentColor === COLOR1) ? nextColor = COLOR2 : nextColor = COLOR1;
          document.getElementById('mouse-tester').style.backgroundColor = nextColor;
        ">
        Click here to change the button color!
      </button>
    </div>
  `;

  return(
    <>
    {(activeAccessMethodName) ?
      null
      : 
      <p><strong>Note: </strong>It looks like you haven't selected an access method yet.<br/>
      If you want to let us know what access method you're using, <Link to={choose_path} >go select a method</Link>, then return here.
      That will make it easier to keep track of your Test-drive results, and let us include the method in your reports</p>
    }
    <hr />
    <p><strong>Running the Test-drive on this computer?</strong> Check Your Setup if you need to check that the access method is working properly.</p>
    <LearnMoreModal 
      color="secondary"
      linkText="Check Your Setup"
      title="Check Your Setup"
      content={ACCESS_TESTER_HTML}
    />
    {' '}
    <LearnMoreModal linkText="" 
      color="secondary"
      title="About the setup checker" 
      content="The setup checker is a way to see if your acccess setup is working properly. It shows you when events occur: like mouse movements, keypresses, and switch presses. 
      It can be a convenient pre-check before starting a Test-drive.  
      Note that it won't be helpful for access methods that you are trying on another device." />
    <hr />
    <p className="aa-review-row">If you're <strong>not</strong> going to run the Test-drive on this computer, set up the access method on whatever device you're going to use.</p>
    <p>When the access method is connected and working properly, hit Continue to move to the next step.</p>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveSetupSandbox(props) {
  const {
    firstName,
    activeAccessMethodName,
    activeSetupSelectionMethod,
    activeSetupBodySite,
    activeSetupAccessories,
    activeSetupName,
    onContinue
  } = props;

  const dev = props.dev || {};

  const pageData = {
    image: "",
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `Make sure everything is set up and working properly`,
    subTitle: `Before you start the Test-drive, set up the access method and help ${firstName} understand how to use it, if necessary.`,
    learnMore: {linkText: "Learn More about getting ready for a Test-drive",
                title: "Getting ready for a Test-drive",
                content: SANDBOX_HELP
                },
    nextRoute: "", //not needed here cuz we're using onContinue
    sidebar: true
  }

  console.log("Rendering TestDriveSetupSandbox");
  
  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <TestDriveSetupSandboxContent activeAccessMethodName={activeAccessMethodName}
        />
      } 
      sidebarComponent={
        dev.showDevSidebar 
        ?
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
        :  
        <TestDriveSetupSidebar 
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={activeSetupSelectionMethod}
          activeSetupBodySite={activeSetupBodySite}
          activeSetupAccessories={activeSetupAccessories}
          activeSetupName={activeSetupName}
       />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

TestDriveSetupSandbox.propTypes = propTypes;
TestDriveSetupSandbox.defaultProps = defaultProps;

export default TestDriveSetupSandbox;